import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import type { UsageFilterState } from './usageFilters';
import { useUsageLabels } from './useUsageLabels';

interface UsageFilterChipsProps {
  state: UsageFilterState;
  onChange: (nextState: UsageFilterState) => void;
}

interface ActiveChip {
  key: string;
  label: string;
  clear: () => void;
}

export function UsageFilterChips({ state, onChange }: UsageFilterChipsProps) {
  const { t } = useTranslation('assistant');
  const { topicLabel, screenLabel } = useUsageLabels();
  const chips: ActiveChip[] = [];
  if (state.tenantId !== null) {
    chips.push({
      key: 'workspace',
      label: t('insights.filters.workspaceChip', {
        name: state.tenantName === null ? state.tenantId : state.tenantName,
      }),
      clear: function clearWorkspace() {
        onChange({ ...state, tenantId: null, tenantName: null });
      },
    });
  }
  if (state.topic !== null) {
    chips.push({
      key: 'topic',
      label: t('insights.filters.topicChip', { name: topicLabel(state.topic) }),
      clear: function clearTopic() {
        onChange({ ...state, topic: null });
      },
    });
  }
  if (state.screen !== null) {
    chips.push({
      key: 'screen',
      label: t('insights.filters.screenChip', { name: screenLabel(state.screen) }),
      clear: function clearScreen() {
        onChange({ ...state, screen: null });
      },
    });
  }
  if (chips.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-2" aria-live="polite">
      <span className="text-xs font-semibold text-zoomuted">{t('insights.filters.active')}</span>
      {chips.map(function renderChip(chip) {
        return (
          <button
            key={chip.key}
            type="button"
            onClick={chip.clear}
            aria-label={t('insights.filters.removeFilter', { name: chip.label })}
            className="px-3 py-1.5 text-xs rounded-lg font-semibold bg-zoovpalelavender text-zooprimary hover:bg-zoopalelavender flex items-center gap-1.5"
          >
            {chip.label}
            <Icon name="X" />
          </button>
        );
      })}
      <Button
        variant="link"
        className="text-xs"
        onClick={function clearAll() {
          onChange({ ...state, tenantId: null, tenantName: null, topic: null, screen: null });
        }}
      >
        {t('insights.filters.clearAll')}
      </Button>
    </div>
  );
}
